// Role capability helpers. Always use these instead of comparing role strings
// directly, so the combined "Facilitator + Admin" role inherits both sides.

export type RoleLike = string | null | undefined;

export function isAdminRole(role: RoleLike): boolean {
  return role === 'admin' || role === 'facilitator_admin' || role === 'god_mode';
}

export function isFacilitatorRole(role: RoleLike): boolean {
  return role === 'facilitator' || role === 'facilitator_admin' || role === 'god_mode';
}

export function isLeadershipRole(role: RoleLike): boolean {
  return isAdminRole(role) || isFacilitatorRole(role);
}

// Platform-owner superuser: the only role that can manage prompts and triage
// org-wide feedback.
export function isGodModeRole(role: RoleLike): boolean {
  return role === 'god_mode';
}

export function isGuestRole(role: RoleLike): boolean {
  return role === 'guest';
}

type AccessLike = { role?: RoleLike; limit_app_access?: boolean; payment_lapsed?: boolean };

function isLimitedMember(user: AccessLike | null | undefined): boolean {
  return Boolean(user?.limit_app_access || user?.payment_lapsed);
}

/**
 * Guests, members whose app access has been limited, and members whose dues
 * lapsed. Billing pause is independent.
 */
export function hasGuestPrivileges(user: AccessLike | null | undefined): boolean {
  return isGuestRole(user?.role) || isLimitedMember(user);
}

/** Everyone may join a snapshot group, including guests and limited members. */
export function canJoinSnapshotGroup(user: AccessLike | null | undefined): boolean {
  return Boolean(user);
}

/** Hosting a snapshot group stays a member surface. */
export function canHostSnapshotGroup(user: AccessLike | null | undefined): boolean {
  return Boolean(user) && !isGuestRole(user?.role) && !isLimitedMember(user);
}

export const ROLE_LABELS: Record<string, string> = {
  member: 'Member',
  advisor: 'Advisor',
  facilitator: 'Facilitator',
  facilitator_admin: 'Facilitator + Admin',
  admin: 'Admin',
  god_mode: 'Super Admin',
  guest: 'Guest',
};

export function roleLabel(role: RoleLike): string {
  if (!role) return '';
  return ROLE_LABELS[role] ?? role.charAt(0).toUpperCase() + role.slice(1);
}

/** Directory / profile title. Facilitator + Admin shows as Facilitator. */
export function publicRoleLabel(role: RoleLike, snapshotType?: string): string {
  if (snapshotType === 'nonprofit') return 'Nonprofit';
  if (isFacilitatorRole(role)) return 'Facilitator';
  if (isAdminRole(role)) return 'Admin';
  if (role === 'advisor') return 'Advisor';
  return 'Member';
}

export function publicRoleBadgeClass(role: RoleLike, snapshotType?: string): string {
  const label = publicRoleLabel(role, snapshotType);
  if (label === 'Facilitator') return 'bg-gold/10 text-gold';
  if (label === 'Admin') return 'bg-navy/10 text-navy';
  if (label === 'Advisor') return 'bg-navy/10 text-navy';
  if (label === 'Nonprofit') return 'bg-navy/10 text-navy';
  return 'bg-gray-100 text-gray-600';
}
